// Loader de posts MDX — importa todos os .mdx da pasta do idioma sob demanda (lazy)
export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  publishedAt: string;
  author: string;
  tags: string[];
  coverImage: string;
  readingTime: string;
  status: string;
  faq?: { q: string; a: string }[];
  /** Slug da praia/página-hub a que este post pertence (cluster). Relacionados ficam dentro do mesmo hub. */
  hub?: string;
  Component: React.ComponentType;
}

const LANG_FOLDER: Record<string, string> = {
  'pt-BR': 'pt',
  pt: 'pt',
  en: 'en',
  es: 'es',
  de: 'de',
  fr: 'fr',
};

function getFolder(lang?: string): string {
  return LANG_FOLDER[lang || ''] || 'pt';
}

function parseSlug(path: string): string {
  const match = path.match(/\/(?<slug>[\w-]+)\.mdx$/);
  return match?.groups?.slug || '';
}

// Vite requer que o path do glob seja literal (não pode ser variável composta)
// Por isso declaramos um glob por pasta — sem eager:true para habilitar lazy chunking
const ptModules = import.meta.glob('./pt/*.mdx');
const enModules = import.meta.glob('./en/*.mdx');
const esModules = import.meta.glob('./es/*.mdx');
const deModules = import.meta.glob('./de/*.mdx');
const frModules = import.meta.glob('./fr/*.mdx');

const modulesByFolder: Record<string, Record<string, () => Promise<unknown>>> = {
  pt: ptModules,
  en: enModules,
  es: esModules,
  de: deModules,
  fr: frModules,
};

// Frontmatter carregado EAGER (só os metadados, sem o componente) — uso SÍNCRONO,
// para renderizar relacionados do cluster no SSR/prerender (SEO).
const metaByFolder: Record<string, Record<string, any>> = {
  pt: import.meta.glob('./pt/*.mdx', { import: 'frontmatter', eager: true }),
  en: import.meta.glob('./en/*.mdx', { import: 'frontmatter', eager: true }),
  es: import.meta.glob('./es/*.mdx', { import: 'frontmatter', eager: true }),
  de: import.meta.glob('./de/*.mdx', { import: 'frontmatter', eager: true }),
  fr: import.meta.glob('./fr/*.mdx', { import: 'frontmatter', eager: true }),
};

export interface PostMeta {
  slug: string;
  title: string;
  excerpt: string;
  coverImage: string;
  hub?: string;
  publishedAt: string;
}

/** Posts de um cluster (hub), SÍNCRONO — para relacionados dentro do hub no SSR. */
export function getPostsMetaByHub(hub: string, lang?: string): PostMeta[] {
  const folder = getFolder(lang);
  const mods = metaByFolder[folder] ?? metaByFolder.pt;
  return Object.entries(mods)
    .map(([path, fm]) => ({
      slug: parseSlug(path),
      title: fm.title || '',
      excerpt: fm.excerpt || '',
      coverImage: fm.coverImage || '/images/hero-bg.jpg',
      hub: fm.hub,
      publishedAt: fm.publishedAt || '',
      status: fm.status || 'published',
    }))
    .filter(p => p.hub === hub && p.status === 'published')
    .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
}

async function loadModules(folder: string): Promise<[string, any][]> {
  const mods = modulesByFolder[folder] ?? ptModules;
  return Promise.all(
    Object.entries(mods).map(async ([path, load]) => {
      const mod = await (load as () => Promise<any>)();
      return [path, mod] as [string, any];
    })
  );
}

function parseEntries(entries: [string, any][]): BlogPost[] {
  const posts: BlogPost[] = entries.map(([path, mod]) => {
    const slug = parseSlug(path);
    const fm = mod.frontmatter || {};
    return {
      slug,
      title: fm.title || slug,
      excerpt: fm.excerpt || '',
      publishedAt: fm.publishedAt || new Date().toISOString(),
      author: fm.author || 'Guia Ilha do Mel',
      tags: fm.tags || [],
      coverImage: fm.coverImage || '/images/hero-bg.jpg',
      readingTime: fm.readingTime || '5 min',
      status: fm.status || 'published',
      faq: fm.faq || undefined,
      hub: fm.hub || undefined,
      Component: mod.default,
    };
  });

  return posts
    .filter(p => p.status === 'published')
    .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
}

export async function getAllPosts(lang?: string): Promise<BlogPost[]> {
  const folder = getFolder(lang);
  const entries = await loadModules(folder);
  return parseEntries(entries);
}

export async function getPostBySlug(slug: string, lang?: string): Promise<BlogPost | undefined> {
  const posts = await getAllPosts(lang);
  return posts.find(p => p.slug === slug);
}

export async function getPostBySlugIncludingDrafts(
  slug: string,
  lang?: string
): Promise<BlogPost | undefined> {
  const folder = getFolder(lang);
  const mods = modulesByFolder[folder] ?? ptModules;

  for (const [filePath, load] of Object.entries(mods)) {
    if (filePath.includes(`/${slug}.mdx`)) {
      const mod = await (load as () => Promise<any>)();
      const fm = mod.frontmatter || {};
      return {
        slug,
        title: fm.title || slug,
        excerpt: fm.excerpt || '',
        publishedAt: fm.publishedAt || new Date().toISOString(),
        author: fm.author || 'Guia Ilha do Mel',
        tags: fm.tags || [],
        coverImage: fm.coverImage || '/images/hero-bg.jpg',
        readingTime: fm.readingTime || '5 min',
        status: fm.status || 'published',
        faq: fm.faq || undefined,
        hub: fm.hub || undefined,
        Component: mod.default,
      };
    }
  }
  return undefined;
}

export async function getPostsByTag(tag: string, lang?: string): Promise<BlogPost[]> {
  const posts = await getAllPosts(lang);
  return posts.filter(p => p.tags.includes(tag));
}
